/**
 * ⛔⛔ THE STALENESS KEY FOR A SECTOR RESOLUTION, IN ONE PLACE.
 *
 * Both CategoryResearch surfaces fire an async resolve-sector lookup and discard any response the
 * page has moved on from. That guard keyed on the category path alone, which was CORRECT while the
 * path was the whole input — and stopped being correct the moment the viewed sector and the
 * candidate sector ids became decisive inputs to the answer (task-2by6ldlmau).
 *
 * ⇒ THE RACE IT LETS THROUGH: switch sector 47 -> 50 on ONE category. Two lookups are in flight for
 * the same path. The slower 47 response lands last, the guard cannot tell it apart, and
 * resolvedCategorySectorId becomes 47 — so the destructive controls re-arm against the sector
 * NOBODY IS VIEWING. That is the same wrong-sector write this PR exists to remove, arriving through
 * a race instead of a key mismatch.
 *
 * 🔑 A GUARD THAT WAS COMPLETE BECOMES PARTIAL THE MOMENT A NEW INPUT IS MADE DECISIVE, without the
 * guard itself being touched — and a partial guard is more dangerous than none, because it reads as
 * handled. Nobody reviewing that line would suspect it: it looks exactly as it did when it was right.
 *
 * ⇒ SO THE KEY LIVES HERE RATHER THAN BEING BUILT INLINE ON EACH PAGE. One definition, so the two
 * surfaces cannot drift apart, and so "does the key carry every decisive input" is a question with
 * one answer instead of two.
 */
export interface SectorResolveKeyInput {
    /** The category being resolved: a full path on the admin page, free text on the perceptions page. */
    category: string;
    /**
     * The sector whose row the operator has open, when the surface has one.
     * ⛔ null is a REAL VALUE here, not a missing one: a surface with no operator-chosen sector
     * (the perceptions page) must key on null rather than on a default it invented, or two
     * genuinely different situations collapse into one key.
     */
    viewedSector?: string | number | null;
    /** Every sector the lookup may choose between — decisive, because it is sent to the route. */
    candidateSectors?: readonly number[] | null;
}

/**
 * A stable string identifying one resolution request by ALL of its inputs.
 *
 * Compare the key captured when the request was issued against the ref at the moment the response
 * lands; if they differ, the page has moved on and the response must be discarded.
 */
export function sectorResolveKey(input: SectorResolveKeyInput): string {
    return JSON.stringify({
        category: input.category ?? '',
        // Normalised to a string because `sectorId` is a string on one page and a number on the
        // other; without this, 47 and "47" would produce different keys for the same situation and
        // every response would look stale.
        viewedSector: input.viewedSector != null ? String(input.viewedSector) : null,
        // NOT sorted: the order of a site's sector_ids is itself meaningful (element 0 is the
        // fallback candidate), so two different orders are two different requests.
        candidateSectors: input.candidateSectors ?? null,
    });
}
